import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, ExternalLink, Info } from "lucide-react";

const DEFAULTS = {
  clickup: { api_token: "", list_id: "", team_id: "" },
  meta: { access_token: "", ad_account_id: "", app_id: "" },
  google: { client_id: "", client_secret: "", refresh_token: "", ga4_property_id: "" },
  meta_demo: { impressions: 245320, clicks: 8712, spend: 12800.55, ctr: 3.55, cpc: 1.47 },
  google_demo: { sessions: 18420, users: 12310, conversions: 245, avg_engagement_time: 132 },
};

export default function ClientIntegrationsEditor() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content/kv/client_integrations").then(({ data }) => {
      if (data) setCfg({ ...DEFAULTS, ...data });
    }).catch(() => {});
  }, []);

  const upd = (group, key, value) => setCfg((s) => ({ ...s, [group]: { ...s[group], [key]: value } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content/kv/client_integrations", { value: cfg });
      toast.success("Integrações salvas.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Integrações Externas</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">Configure ClickUp, Meta Ads e Google Ads/Analytics. Enquanto as chaves estiverem vazias, o portal usa dados de demonstração editáveis abaixo.</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="integrations-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {busy ? "Salvando…" : "Salvar"}</button>
      </div>

      <Panel title="ClickUp" description="Cria tarefas automaticamente quando um cliente envia uma demanda. Obtenha em app.clickup.com → Settings → Apps → API Token. O list_id vem da URL: /list/{ID}.">
        <F label="API Token"><input type="password" value={cfg.clickup.api_token} onChange={(e) => upd("clickup", "api_token", e.target.value)} placeholder="pk_… ou token pessoal" className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" data-testid="integ-clickup-token" /></F>
        <F label="List ID (onde criar tarefas)"><input value={cfg.clickup.list_id} onChange={(e) => upd("clickup", "list_id", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" data-testid="integ-clickup-list" /></F>
        <F label="Team ID (opcional)"><input value={cfg.clickup.team_id} onChange={(e) => upd("clickup", "team_id", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" /></F>
      </Panel>

      <Panel title="Meta Ads (Facebook Marketing API)" description="Impressões, cliques, spend, CTR, CPC. Requer acesso a act_{AD_ACCOUNT_ID}.">
        <F label="Access Token (System User ou Long-lived)"><input type="password" value={cfg.meta.access_token} onChange={(e) => upd("meta", "access_token", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" data-testid="integ-meta-token" /></F>
        <F label="Ad Account ID (sem o prefixo 'act_')"><input value={cfg.meta.ad_account_id} onChange={(e) => upd("meta", "ad_account_id", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" data-testid="integ-meta-account" /></F>
        <F label="App ID (opcional)"><input value={cfg.meta.app_id} onChange={(e) => upd("meta", "app_id", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" /></F>
      </Panel>

      <Panel title="Google Ads / Analytics (GA4)" description="Requer OAuth2. Client ID/Secret vêm do Google Cloud Console. Refresh Token é gerado no consentimento inicial.">
        <F label="Client ID"><input value={cfg.google.client_id} onChange={(e) => upd("google", "client_id", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" data-testid="integ-google-client" /></F>
        <F label="Client Secret"><input type="password" value={cfg.google.client_secret} onChange={(e) => upd("google", "client_secret", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" data-testid="integ-google-secret" /></F>
        <F label="Refresh Token"><input type="password" value={cfg.google.refresh_token} onChange={(e) => upd("google", "refresh_token", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" /></F>
        <F label="GA4 Property ID"><input value={cfg.google.ga4_property_id} onChange={(e) => upd("google", "ga4_property_id", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" /></F>
      </Panel>

      <Panel title="Dados de Demonstração (fallback)" description="Valores exibidos enquanto as chaves reais não estão configuradas. Você pode editar para uma demo mais realista.">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent-orange mb-2">Meta demo</div>
            {["impressions", "clicks", "spend", "ctr", "cpc"].map((k) => (
              <F key={k} label={k}><input type="number" step="any" value={cfg.meta_demo[k]} onChange={(e) => upd("meta_demo", k, Number(e.target.value))} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
            ))}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-accent-orange mb-2">Google demo</div>
            {["sessions", "users", "conversions", "avg_engagement_time"].map((k) => (
              <F key={k} label={k}><input type="number" step="any" value={cfg.google_demo[k]} onChange={(e) => upd("google_demo", k, Number(e.target.value))} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></F>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
}

function Panel({ title, description, children }) {
  return (
    <section className="border border-black/10 rounded-md p-5 mb-4">
      <div className="font-display text-xl mb-1">{title}</div>
      <p className="text-xs text-neutral-500 mb-4 flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 shrink-0" /> {description}</p>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function F({ label, children }) {
  return (<div><label className="text-xs text-neutral-500">{label}</label>{children}</div>);
}
